import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { AuthGate } from './core/auth/AuthGate'
import { UserProvider } from './core/auth/UserProvider'
import { ToastProvider } from './core/ui/toast/ToastProvider'
import { ErrorBoundary } from './core/errors/ErrorBoundary'
import OfflineBanner from './core/errors/OfflineBanner'
import { ThemeProvider } from './core/theme/ThemeProvider'
import { initAnalytics } from './analytics'
// Redesign tokens + chrome primitives — loaded FIRST so existing index.css
// overrides still win the cascade while new .ui-* classes from ui.css are
// available to new modules.
import './styles/tokens.css'
import './styles/ui.css'
import './index.css'
import './App.css'
// ⬇️ NEW — must be imported AFTER index.css so its overrides win cascade order.
import './styles/cross-browser-fixes.css'
// SurfaceHeader — component styles + cross-surface normaliser in one
// file. Normalises every legacy module header (.chatroom-header,
// .profile-header, .education-header, .networking-header, etc.) to
// match the SSOT spec: 64px height, 16px padding, bg-canvas,
// border-bottom, display-font 18px title. Loaded LAST (after
// cross-browser-fixes) so its !important rules beat per-module CSS.
import './core/layout/SurfaceHeader.css'
// Typography SSOT — every font-family / size / weight mapped to the
// redesign token scale (--text-xs..5xl, --font-sans/display/mono).
// Loaded LAST so it wins the cascade for cross-surface consistency.
import './styles/typography.css'
// Typography primitives — <Title>, <Text>, <Link> component styles.
// Loaded AFTER typography.css so primitive classes can reference the
// same tokens the normalizer installs.
import './core/ui/typography/Typography.css'
// Surface-bg SSOT — forces every module's main-pane root class to
// render on var(--bg-app) so the app reads as one palette and not
// a patchwork of per-module colours.
import './styles/surface-bg.css'

// Boot analytics (loads GA4 only if consent was previously granted)
initAnalytics();

// Pre-React theme application — avoids a flash of incorrect theme on boot.
// ThemeProvider picks up the same localStorage key once React mounts.
(function bootTheme() {
  const stored = localStorage.getItem('chiefy.theme');
  // Legacy preference migration: if the old boolean key exists but the
  // new tri-state key doesn't, carry the user's choice forward.
  const legacy = localStorage.getItem('pref_dark_mode');
  const choice = (stored === 'light' || stored === 'dark' || stored === 'system')
    ? stored
    : legacy === 'false'
      ? 'light'
      : legacy === 'true'
        ? 'dark'
        : 'system';
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
  const resolved = choice === 'system' ? (prefersDark ? 'dark' : 'light') : choice;
  document.documentElement.setAttribute('data-theme', resolved);
})();

// iOS Safari: add touchstart listener so the browser treats elements as "touchable"
// and fires click events immediately without waiting for double-tap
document.addEventListener('touchstart', () => {}, { passive: true });

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <OfflineBanner />
      <ThemeProvider>
        <ToastProvider>
          <AuthGate>
            <UserProvider>
              <App />
            </UserProvider>
          </AuthGate>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
